import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Play, CheckCircle, AlertCircle, Clock, Zap, SkipForward, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const statusStyles = {
  ok: { icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-500/10 border-green-500/30' },
  error: { icon: AlertCircle, color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/30' },
  skipped: { icon: SkipForward, color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/30' },
  halted: { icon: AlertCircle, color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/30' },
  missing: { icon: AlertCircle, color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/30' },
};

/**
 * Dry-run a journey against a real order/customer. Shows a step-by-step trace
 * — condition evaluations with actual values, rendered WhatsApp/Email previews,
 * skipped-node reasons — WITHOUT any side effects (no real sends, no DB writes).
 */
export default function TestRunDialog({ journey, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [entityType, setEntityType] = useState('order');
  const [selectedId, setSelectedId] = useState('');
  const [result, setResult] = useState(null);

  const token = () => localStorage.getItem('token');
  const journeyId = journey?.id;

  const loadCandidates = useCallback(async () => {
    if (!journeyId) return;
    try {
      const r = await axios.get(`${API}/journeys/${journeyId}/test-candidates`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setEntityType(r.data.entity_type || 'order');
      setCandidates(r.data.candidates || []);
      if ((r.data.candidates || []).length) {
        setSelectedId(r.data.candidates[0].id);
      }
    } catch (e) {
      toast.error('Could not load test candidates');
    }
  }, [journeyId]);

  useEffect(() => {
    if (open) {
      setResult(null);
      loadCandidates();
    }
  }, [open, loadCandidates]);

  const runTest = async () => {
    if (!selectedId) {
      toast.error('Pick an entity first');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const r = await axios.post(
        `${API}/journeys/${journeyId}/test-run`,
        { entity_type: entityType, entity_id: selectedId },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setResult(r.data);
      if (r.data.error) {
        toast.warning(r.data.error);
      } else {
        toast.success(`Test run complete — ${r.data.total_steps} step(s) traced`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test run failed');
      setResult({ error: e?.response?.data?.detail || 'Failed' });
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div
        className="bg-wt-paper border border-wt-border rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="test-run-dialog"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-wt-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-wt-gold/10 rounded-lg">
              <Play className="h-5 w-5 text-wt-gold" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-wt-text-primary">Test Run</h3>
              <p className="text-xs text-wt-text-muted">
                Dry-run this journey against a real {entityType}. No messages sent, no DB writes.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-wt-subtle rounded-lg" data-testid="test-run-close">
            <X className="h-5 w-5 text-wt-text-muted" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Entity picker */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-wt-text-primary capitalize">Pick a test {entityType}</label>
            <div className="flex gap-2">
              <Select value={selectedId} onValueChange={setSelectedId}>
                <SelectTrigger className="bg-wt-bg border-wt-border flex-1" data-testid="test-run-entity-select">
                  <SelectValue placeholder={`Select a recent ${entityType}`} />
                </SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border max-h-72">
                  {candidates.length === 0 && (
                    <SelectItem value="__empty__" disabled>
                      No recent {entityType}s found
                    </SelectItem>
                  )}
                  {candidates.map((c) => (
                    <SelectItem key={c.id} value={c.id} className="text-wt-text-primary">
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={runTest}
                disabled={loading || !selectedId || selectedId === '__empty__'}
                className="bg-wt-gold hover:bg-wt-gold/90 text-black font-medium"
                data-testid="test-run-start-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                <span className="ml-1.5">{loading ? 'Running…' : 'Run test'}</span>
              </Button>
            </div>
          </div>

          {/* Trace results */}
          {result && (
            <div className="space-y-3" data-testid="test-run-trace">
              {result.error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  <AlertCircle className="h-4 w-4 inline mr-1.5" />
                  {result.error}
                </div>
              )}

              {result.trace?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wide text-wt-text-muted">
                    Execution trace — {result.total_steps} step(s)
                  </p>
                  {result.trace.map((step, idx) => {
                    const s = statusStyles[step.status] || statusStyles.ok;
                    const Icon = s.icon;
                    return (
                      <div
                        key={`${step.node_id}-${idx}`}
                        className={`p-3 border rounded-lg ${s.bg}`}
                        data-testid={`test-run-step-${idx}`}
                      >
                        <div className="flex items-start gap-2">
                          <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${s.color}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-semibold text-wt-text-muted">Step {idx + 1}</span>
                              <span className="text-xs text-wt-gold uppercase tracking-wide">
                                {step.node_type}
                              </span>
                              <span className="text-sm text-wt-text-primary font-medium">
                                {step.label}
                              </span>
                            </div>
                            {step.details && Object.keys(step.details).length > 0 && (
                              <div className="mt-2 space-y-1">
                                {Object.entries(step.details).map(([k, v]) => (
                                  <div key={k} className="flex gap-2 text-xs">
                                    <span className="text-wt-text-muted min-w-[100px] capitalize">
                                      {k.replace(/_/g, ' ')}:
                                    </span>
                                    <span className="text-wt-text-primary break-all whitespace-pre-wrap flex-1">
                                      {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Raw entity data (collapsible) */}
              {result.entity_data && (
                <details className="bg-wt-subtle border border-wt-border rounded-lg">
                  <summary className="p-3 cursor-pointer text-xs font-medium text-wt-text-muted hover:text-wt-text-primary">
                    Entity data (raw — variables resolve from this)
                  </summary>
                  <pre className="p-3 pt-0 text-[10px] text-wt-text-muted overflow-auto max-h-64">
                    {JSON.stringify(result.entity_data, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          )}

          {!result && !loading && (
            <div className="text-center p-8 text-xs text-wt-text-muted border border-dashed border-wt-border rounded-lg">
              <Zap className="h-5 w-5 mx-auto mb-2 text-wt-text-muted" />
              Pick an entity and click <strong>Run test</strong> to see how this journey would execute.
              <br />
              Condition evaluations, skipped nodes, rendered WhatsApp/Email previews — all visible here before you activate.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
